import {
  Dropdown,
  Icon,
  InputMasked,
  InputText,
  RadioButton,
} from "@uigovpe/components";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useZipCode } from "@/hooks/profile/useZipCode";
import { lookupZipCode } from "@/infrastructure/services/profileService";
import { cityOptions } from "@/constants/cities_pe";
import type { ProfileFormData } from "../../../types";
import { onlyDigits } from "@/utils/formats";
import LockedField from "../locked-field";

export default function ProfileStepThree() {
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ProfileFormData>();
  const zipCode = watch("zipCode");
  const preference = watch("locationDisplayPreference");
  const { error: cepError, isLoading, searchZipCode, setError: setCepError } = useZipCode();
  const [isCepFound, setIsCepFound] = useState(false);

  useEffect(() => {
    if (onlyDigits(zipCode).length !== 8) {
      setIsCepFound(false);
      setCepError("");
      return;
    }

    let ignore = false;
    searchZipCode(zipCode)
      .then((data) => {
        if (ignore) return;
        setValue("address", data.logradouro, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("neighborhood", data.bairro, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue("city", data.localidade, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setIsCepFound(true);
        setCepError("");
      })
      .catch((error) => {
        if (ignore) return;
        setIsCepFound(false);
        setCepError(
          error instanceof Error ? error.message : "CEP não encontrado.",
        );
      })

    return () => {
      ignore = true;
    };
  }, [searchZipCode, setCepError, setValue, zipCode]);

  const selectPreference = (
    value: ProfileFormData["locationDisplayPreference"],
  ) =>
    setValue("locationDisplayPreference", value, {
      shouldDirty: true,
      shouldValidate: true,
    });

  return (
    <div className="flex! w-full! flex-col! gap-8!">
      <section className="flex! flex-col! gap-5!">
        <div className="flex! flex-col! gap-2!">
          <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
            <Icon icon="location_on" className="text-primary!" />
            <h3 className="m-0! text-base! font-bold!">
              Localização do negócio
            </h3>
          </div>
          <p className="m-0! text-sm! text-zinc-700!">
            Informe onde seu negócio está localizado ou onde você realiza
            atendimentos.
          </p>
        </div>
        <div className="grid! grid-cols-1! gap-x-6! gap-y-5! md:grid-cols-6!">
          <div className="md:col-span-2!">
            <Controller
              name="zipCode"
              control={control}
              rules={{
                required: "Informe o CEP",
                minLength: { value: 8, message: "Informe um CEP completo" },
                validate: async (value) => {
                  if (onlyDigits(value).length !== 8) {
                    return "Informe um CEP completo";
                  }
                  try {
                    await lookupZipCode(value);
                    return true;
                  } catch (error) {
                    return error instanceof Error
                      ? error.message
                      : "Não foi possível validar o CEP. Tente novamente.";
                  }
                },
              }}
              render={({ field, fieldState }) => (
                <InputMasked
                  label="CEP"
                  mask="99999-999"
                  placeholder="00000-000"
                  value={field.value}
                  supportText={
                    fieldState.error?.message ??
                    (cepError ||
                      (isLoading
                        ? "Buscando CEP..."
                        : "Informe o CEP do local de atendimento."))
                  }
                  invalid={Boolean(fieldState.error || cepError)}
                  className="w-full!"
                  onBlur={field.onBlur}
                  onChange={(event) =>
                    field.onChange(onlyDigits(event.target.value))
                  }
                />
              )}
            />
          </div>
          <div className="md:col-span-3!">
            <LockedField id="profile-address-by-cep" locked={isCepFound} tooltip="Este campo foi preenchido automaticamente a partir do CEP.">
              <InputText
                label="Endereço"
                placeholder="Ex: Rua das Flores"
                disabled={isCepFound}
                supportText={
                  errors.address?.message ??
                  "Nome da rua, avenida ou local de referência."
                }
                invalid={Boolean(errors.address)}
                className="w-full!"
                {...register("address", { required: "Informe o endereço" })}
              />
            </LockedField>
          </div>
          <div className="md:col-span-1!">
            <InputText
              label="Número"
              placeholder="Ex: 123"
              supportText={
                errors.addressNumber?.message ??
                'Se não houver número, informe "S/N".'
              }
              invalid={Boolean(errors.addressNumber)}
              className="w-full!"
              {...register("addressNumber", { required: "Informe o número" })}
            />
          </div>
          <div className="md:col-span-2!">
            <InputText
              label="Complemento"
              placeholder="Ex: Sala 2, bloco B"
              supportText="Opcional. Use para detalhes do endereço."
              className="w-full!"
              {...register("addressComplement")}
            />
          </div>
          <div className="md:col-span-2!">
            <LockedField id="profile-neighborhood-by-cep" locked={isCepFound} tooltip="Este campo foi preenchido automaticamente a partir do CEP.">
              <InputText
                label="Bairro"
                placeholder="Ex: Boa Vista"
                disabled={isCepFound}
                supportText={
                  errors.neighborhood?.message ??
                  "Bairro onde o negócio está localizado."
                }
                invalid={Boolean(errors.neighborhood)}
                className="w-full!"
                {...register("neighborhood", { required: "Informe o bairro" })}
              />
            </LockedField>
          </div>
          <div className="md:col-span-2!">
            <LockedField id="profile-city-by-cep" locked={isCepFound} tooltip="Este campo foi preenchido automaticamente a partir do CEP.">
              <Controller
                name="city"
                control={control}
                rules={{ required: "Selecione o município" }}
                render={({ field, fieldState }) => (
                  <Dropdown
                    label="Município"
                    value={field.value}
                    options={cityOptions}
                    disabled={isCepFound}
                    placeholder="Selecione o município"
                    supportText={
                      fieldState.error?.message ??
                      "Cidade onde seu negócio atua em Pernambuco."
                    }
                    invalid={Boolean(fieldState.error)}
                    className="w-full!"
                    onChange={(event) => field.onChange(event.value)}
                  />
                )}
              />
            </LockedField>
          </div>
        </div>
      </section>

      <section className="flex! flex-col! gap-5!">
        <div className="flex! flex-col! gap-2!">
          <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
            <Icon icon="add_business" className="text-primary!" />
            <h3 className="m-0! text-base! font-bold!">
              Preferências de exibição
            </h3>
          </div>
          <p className="m-0! text-sm! text-zinc-700!">
            Escolha como sua localização aparecerá na vitrine pública.
          </p>
        </div>
        <fieldset>
          <legend className="sr-only!">Preferência de exibição</legend>
          <div className="grid! grid-cols-1! gap-6! md:grid-cols-3!">
            <Preference
              label="Bairro e município"
              detail="Recomendado para manter sua privacidade."
              value="neighborhood_city"
              selected={preference}
              onSelect={selectPreference}
            />
            <Preference
              label="Endereço completo"
              detail="Exibe rua, número, bairro e município."
              value="full_address"
              selected={preference}
              onSelect={selectPreference}
            />
            <Preference
              label="Online ou sob consulta"
              detail="Exibe o município e orienta o contato antes do atendimento."
              value="online_or_on_request"
              selected={preference}
              onSelect={selectPreference}
            />
          </div>
        </fieldset>
      </section>
    </div>
  );
}

function Preference({
  label,
  detail,
  value,
  selected,
  onSelect,
}: {
  label: string;
  detail: string;
  value: ProfileFormData["locationDisplayPreference"];
  selected: ProfileFormData["locationDisplayPreference"];
  onSelect: (value: ProfileFormData["locationDisplayPreference"]) => void;
}) {
  return (
    <label className="flex! cursor-pointer! items-start! gap-3!">
      <RadioButton
        name="location-preference"
        value={value}
        checked={selected === value}
        onChange={() => onSelect(value)}
      />
      <span className="flex! flex-col! gap-2! text-sm!">
        <span>{label}</span>
        <span className="text-xs! text-zinc-700!">{detail}</span>
      </span>
    </label>
  );
}
